import { delay } from '../utils/misc';

/**
 * Simple client-side wrapper to transcribe an audio Blob using a backend endpoint.
 * Falls back to a mock response in development when no endpoint is configured.
 *
 * ENV:
 *  VITE_TRANSCRIPTION_ENDPOINT – URL of POST endpoint that accepts multipart/form-data {file} and returns { text: string }
 *  VITE_OPENAI_API_KEY – OpenAI API key for Whisper transcription (client-side; dev only)
 */
export async function transcribeAudio(blob: Blob): Promise<string> {
  try {
    const endpoint = import.meta.env.VITE_TRANSCRIPTION_ENDPOINT;
    const openAiKey = import.meta.env.VITE_OPENAI_API_KEY;

    console.info('[transcriptionService] Starting transcription. Blob size:', blob.size, 'type:', blob.type);

    // Skip placeholder URLs to avoid CORS errors
    const isPlaceholderUrl = endpoint && (
      endpoint.includes('your-server.com') || 
      endpoint.includes('localhost') ||
      endpoint.includes('example.com')
    );

    // 1. Preferred: custom backend endpoint (only if not a placeholder)
    if (endpoint && !isPlaceholderUrl) {
      try {
        console.info('[transcriptionService] Using custom endpoint', endpoint);
        const formData = new FormData();
        formData.append('file', blob, 'audio.webm');

        const res = await fetch(endpoint, { 
          method: 'POST', 
          body: formData,
          // Add timeout to prevent hanging
          signal: AbortSignal.timeout(10000)
        });
        
        console.info('[transcriptionService] Endpoint response status', res.status);
        if (!res.ok) throw new Error(`Transcription request failed – ${res.status}`);

        const data = (await res.json()) as { text?: string };
        console.debug('[transcriptionService] Endpoint response json', data);
        if (!data.text) throw new Error('No text in transcription response');
        return data.text;
      } catch (endpointError) {
        console.warn('[transcriptionService] Custom endpoint failed, falling back to alternatives:', endpointError);
        // Continue to next fallback
      }
    } else if (endpoint && isPlaceholderUrl) {
      console.warn('[transcriptionService] Skipping placeholder URL:', endpoint);
    }

    // 2. Fallback: call OpenAI Whisper directly if key provided (client-side; dev only)
    if (openAiKey) {
      try {
        console.info('[transcriptionService] Falling back to OpenAI Whisper');
        const formData = new FormData();
        formData.append('file', blob, 'audio.wav');
        formData.append('model', 'whisper-1');

        const res = await fetch('https://api.openai.com/v1/audio/transcriptions', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${openAiKey}`,
          },
          body: formData,
          signal: AbortSignal.timeout(15000)
        });

        console.info('[transcriptionService] OpenAI response status', res.status);

        if (!res.ok) throw new Error(`OpenAI transcription failed – ${res.status}`);
        const data = (await res.json()) as { text?: string };
        console.debug('[transcriptionService] OpenAI response json', data);
        if (!data.text) throw new Error('No text in OpenAI response');
        return data.text;
      } catch (openAiError) {
        console.warn('[transcriptionService] OpenAI fallback failed:', openAiError);
        // Continue to mock fallback
      }
    }
  } catch (err) {
    console.error('[transcriptionService] Error during transcription', err);
  }

  // Development fallback – simulate Whisper output
  console.info('[transcriptionService] Using mock transcription fallback');
  await delay(1500);
  // Always use the specific mock text for consistency during testing
  const mockText = "I have a sore throat and a slight fever";
  console.info('[transcriptionService] Mock transcription result:', mockText);
  return mockText;
}
